import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BriefcaseBusiness, Loader2, Search, X } from 'lucide-react';
import Pagination from '../common/Pagination';

const pageSize = 10;

const getOperationLabel = (operation) => operation?.projectNo || operation?.name || 'Numarasız Operasyon';

const getAccountName = (operation) => operation?.account?.name || 'Bağımsız Operasyon';

const getAddressText = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;

  return [
    address.countryId?.name,
    address.cityId?.name,
    address.districtId?.name,
    address.fullAddress
  ].filter(Boolean).join(' ');
};

const OperationSearchDialog = ({ isOpen, onClose, onSelect, disabled = false }) => {
  const [search, setSearch] = useState('');
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isOpen) return;

    let ignore = false;

    const fetchOperations = async () => {
      setLoading(true);
      setSearch('');
      setPage(1);

      try {
        const res = await axios.get('/api/operations', { params: { viewType: 'all' } });
        if (ignore) return;
        const data = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
        setOperations(data);
      } catch {
        if (!ignore) setOperations([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchOperations();

    return () => {
      ignore = true;
    };
  }, [isOpen]);

  const filteredOperations = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr-TR');
    if (!term) return operations;

    return operations.filter((operation) => {
      const searchable = [
        operation.projectNo,
        operation.name,
        operation.status,
        operation.operationType,
        operation.transportMode,
        operation.serviceType,
        getAddressText(operation.pickupAddress),
        getAddressText(operation.deliveryAddress),
        getAccountName(operation)
      ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');

      return searchable.includes(term);
    });
  }, [operations, search]);

  const totalPages = Math.max(1, Math.ceil(filteredOperations.length / pageSize));
  const visibleOperations = filteredOperations.slice((page - 1) * pageSize, page * pageSize);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleClose = () => {
    setSearch('');
    setPage(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
      <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 animate-in zoom-in-95">
        <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-3 ml-2">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
              <BriefcaseBusiness size={20} />
            </div>
            <h3 className="font-black text-slate-800 uppercase tracking-tighter">Operasyon Seçimi</h3>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-200 rounded-full transition-all">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              autoFocus
              type="text"
              placeholder="Operasyon no, cari veya durum ile ara..."
              className="w-full pl-14 pr-6 py-4 bg-slate-50 rounded-2xl border-none font-bold text-slate-700 outline-none focus:ring-2 ring-indigo-500/20 transition-all"
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

        <div className="px-4 pb-4 max-h-87.5 overflow-y-auto min-h-75">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="animate-spin text-indigo-600" />
              <p className="text-[10px] font-black text-slate-400 uppercase">Operasyonlar Yükleniyor...</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {visibleOperations.length > 0 ? visibleOperations.map((operation) => (
                <button
                  key={operation._id}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(operation)}
                  className="flex items-center justify-between gap-4 p-4 rounded-2xl transition-all border border-transparent text-left group hover:bg-indigo-50 hover:border-indigo-100 disabled:opacity-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-800 group-hover:text-indigo-700 truncate">
                      {getOperationLabel(operation)}
                    </div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-tighter truncate">
                      {getAccountName(operation)} • {operation.operationType || 'None'} • {operation.status || 'Durum Yok'} • {getAddressText(operation.pickupAddress) || 'Alım adresi yok'}
                    </div>
                  </div>
                  <div className="shrink-0 text-[10px] font-black bg-white px-3 py-1 rounded-lg border border-slate-100 text-slate-400 group-hover:text-indigo-600 group-hover:border-indigo-200">
                    SEÇ
                  </div>
                </button>
              )) : (
                <div className="py-20 text-center">
                  <p className="text-xs font-bold text-slate-400 uppercase">Kayıt Bulunamadı</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-50">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default OperationSearchDialog;
